import React from 'react';
import { OptImg } from '../../ui/OptImg';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import { SmartLink, SectionShell, SectionHeader, isDarkBg } from '../shared';

const COLS = { cards_2: 'sm:grid-cols-2', cards_3: 'sm:grid-cols-2 lg:grid-cols-3', cards_4: 'sm:grid-cols-2 lg:grid-cols-4', logo_cards: 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4', horizontal: 'grid-cols-1 max-w-4xl' };

export const CardGridSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  const layout = section.layout || 'cards_3';
  const items = p.items || [];

  if (layout === 'product_cards') {
    return (
      <SectionShell section={section}>
        {(p.eyebrow || p.title) && (
          <div className={`mb-10 ${section.style?.align === 'left' ? '' : 'text-center'}`}>
            {p.eyebrow && <span className={`text-xs font-medium tracking-wider uppercase ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
            {p.title && <h2 className={`mt-2 !text-3xl ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>}
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-5">
          {items.map((it, i) => {
            const bullets = (it.bullets || '').split(',').map((b) => b.trim()).filter(Boolean);
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="group bg-white border border-neutral-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow text-neutral-900 flex flex-col" data-testid={`card-item-${i}`}>
                {it.image?.url && <div className="aspect-[16/10] overflow-hidden"><OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={it.image.url} alt={it.image.alt_text || it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>}
                <div className="p-4 flex-1 flex flex-col">
                  <h4 className="!font-medium !text-lg mb-1">{it.title}</h4>
                  {it.description && <p className="text-neutral-500 text-sm mb-3">{it.description}</p>}
                  {bullets.length > 0 && <ul className="space-y-1 mb-4">{bullets.map((b) => <li key={b} className="flex items-center gap-2 text-xs text-neutral-600"><Check className="w-3 h-3 text-green-500" />{b}</li>)}</ul>}
                  {it.note_text && it.note_link && (
                    <SmartLink to={it.note_link} className="mb-4 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 underline underline-offset-2" data-testid={`card-note-link-${i}`}>
                      {it.note_text}<ArrowRight className="w-3 h-3" />
                    </SmartLink>
                  )}
                  {it.link && (
                    <SmartLink to={it.link} className="mt-auto inline-flex items-center justify-center gap-2 w-full h-8 border border-input shadow-sm px-3 text-sm font-medium rounded-md group-hover:bg-neutral-900 group-hover:text-white transition-colors">
                      {it.link_text || 'Learn more'}<ChevronRight className="w-3 h-3 ml-1" />
                    </SmartLink>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </SectionShell>
    );
  }

  if (layout === 'logo_cards' || layout === 'logo_cards_3') {
    // Mirrors the coded PartnersPage cards (pill eyebrow, white rounded cards, logo well, hover "Learn More").
    return (
      <SectionShell section={section}>
        {(p.eyebrow || p.title || p.body) && (
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            {p.eyebrow && <span className="inline-block px-4 py-2 bg-neutral-100 text-neutral-600 text-sm font-medium rounded-full mb-4">{p.eyebrow}</span>}
            {p.title && <h2 className={`!text-3xl md:!text-4xl !leading-tight mb-4 ${dark ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h2>}
            {p.body && <p className={`text-lg max-w-2xl mx-auto ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{p.body}</p>}
          </motion.div>
        )}
        <div className={layout === 'logo_cards_3' ? 'grid md:grid-cols-3 gap-8 max-w-5xl mx-auto' : 'grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'}>
          {items.map((it, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="group" data-testid={`card-item-${i}`}>
              <SmartLink to={it.link} className="block">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 hover:shadow-xl hover:border-amber-200 transition-all duration-300 h-full">
                  <div className="relative aspect-[3/2] mb-5 overflow-hidden rounded-lg bg-neutral-50 flex items-center justify-center group-hover:bg-amber-50/50 transition-colors p-6">
                    {it.image?.url ? <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={it.image.url} alt={it.image.alt_text || `${it.title} logo`} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" style={{ maxHeight: '80px' }} loading="lazy" /> : <span className="font-serif text-2xl text-neutral-500 text-center" data-testid={`card-wordmark-${i}`}>{it.title}</span>}
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/10 to-amber-500/0 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-[-100%] group-hover:translate-x-[100%] duration-1000" />
                  </div>
                  <h3 className="!text-lg !font-semibold text-neutral-900 mb-2 group-hover:text-amber-700 transition-colors">{it.title}</h3>
                  {it.description && <p className="text-neutral-600 text-sm leading-relaxed mb-4 line-clamp-3">{it.description}</p>}
                  {it.link && <div className="flex items-center gap-2 text-amber-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity"><span>{it.link_text || 'Learn More'}</span><ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>}
                </div>
              </SmartLink>
            </motion.div>
          ))}
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell section={section}>
      <SectionHeader props={p} style={{ align: section.style?.align || 'center' }} dark={dark} />
      <div className={`grid gap-6 lg:gap-8 ${COLS[layout] || COLS.cards_3}`}>
        {items.map((it, i) => {
          const bullets = (it.bullets || '').split(',').map((b) => b.trim()).filter(Boolean);
          const body = (
            <>
              {it.image?.url && (layout === 'logo_cards'
                ? <div className="h-24 flex items-center justify-center mb-6 bg-neutral-50 border border-neutral-100 px-6"><OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={it.image.url} alt={it.image.alt_text || it.title} className="max-h-20 max-w-full object-contain" loading="lazy" /></div>
                : <OptImg sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" src={it.image.url} alt={it.image.alt_text || it.title} className={`object-cover rounded-[var(--site-radius)] ${layout === 'horizontal' ? 'w-full sm:w-64 aspect-[4/3]' : 'w-full aspect-[4/3] mb-5'}`} loading="lazy" />)}
              <div className="flex-1">
                <h3 className={`${layout === 'logo_cards' ? '!text-lg' : '!text-xl'} !font-semibold text-neutral-900 group-hover:text-[color:var(--site-accent)] transition-colors`}>{it.title}</h3>
                {it.description && <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{it.description}</p>}
                {bullets.length > 0 && <ul className="mt-4 space-y-1.5">{bullets.map((b) => <li key={b} className="flex items-center gap-2 text-sm text-neutral-700"><Check className="w-4 h-4 text-[color:var(--site-accent)]" />{b}</li>)}</ul>}
                {it.link && <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[color:var(--site-accent)]">{it.link_text || 'Learn more'}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>}
              </div>
            </>
          );
          const cls = `group block h-full bg-white border border-neutral-200 p-6 rounded-[var(--site-radius)] hover:border-neutral-400 hover:shadow-lg transition-all ${layout === 'horizontal' ? 'flex flex-col sm:flex-row gap-6' : 'flex flex-col'}`;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.08 }} data-testid={`card-item-${i}`}>
              {it.link ? <SmartLink to={it.link} className={cls}>{body}</SmartLink> : <div className={cls}>{body}</div>}
            </motion.div>
          );
        })}
      </div>
    </SectionShell>
  );
};
